<script setup>
/* eslint-disable no-use-before-define */
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useRoute } from 'vue-router';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const store = useStore();
const route = useRoute();
const { t } = useI18n();
const showAlert = useAlert;

// ─── State ──────────────────────────────────────────────────────────────────
const status = ref('unknown');
const isLoading = ref(false);
const qrCode = ref(null);
const showQr = ref(false);
const settingsData = ref({
  rejectCall: false,
  msgCall: '',
  groupsIgnore: false,
  alwaysOnline: false,
  readMessages: false,
  readStatus: false,
});
const proxyData = ref({
  enabled: false,
  host: '',
  port: '',
  protocol: 'http',
  username: '',
  password: '',
});
const savingSettings = ref(false);
const savingProxy = ref(false);

// ─── Computed ────────────────────────────────────────────────────────────────
const accountId = computed(
  () => store.getters.getCurrentAccountId || route.params.accountId
);
const inboxId = computed(() => props.inbox.id);
const provider = computed(() => props.inbox.provider);
const isEvoApi = computed(() => provider.value === 'evolution_api');

const accessToken = computed(() => store.getters.getCurrentUser?.access_token);
const authHeaders = computed(() => ({
  'Content-Type': 'application/json',
  api_access_token: accessToken.value,
}));

const baseUrl = computed(() =>
  isEvoApi.value
    ? `/api/v1/accounts/${accountId.value}/evolution_api/inboxes/${inboxId.value}`
    : `/api/v1/accounts/${accountId.value}/evolution/inboxes/${inboxId.value}`
);

const statusClass = computed(() => {
  const map = {
    open: 'bg-g-100 text-g-700',
    connected: 'bg-g-100 text-g-700',
    connecting: 'bg-y-100 text-y-700',
    qr_code: 'bg-y-100 text-y-700',
    close: 'bg-r-100 text-r-700',
    unknown: 'bg-n-50 text-n-slate-11',
  };
  return map[status.value] || map.unknown;
});

const statusLabel = computed(() => {
  const map = {
    open: t('INBOX_MGMT.INSTANCE.STATUS.OPEN'),
    connected: t('INBOX_MGMT.INSTANCE.STATUS.OPEN'),
    connecting: t('INBOX_MGMT.INSTANCE.STATUS.CONNECTING'),
    qr_code: t('INBOX_MGMT.INSTANCE.STATUS.QR_CODE'),
    close: t('INBOX_MGMT.INSTANCE.STATUS.CLOSE'),
    unknown: t('INBOX_MGMT.INSTANCE.STATUS.UNKNOWN'),
  };
  return map[status.value] || map.unknown;
});

// ─── API helpers ─────────────────────────────────────────────────────────────
async function apiFetch(path, options = {}) {
  const res = await fetch(path, { headers: authHeaders.value, ...options });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

// ─── Status ──────────────────────────────────────────────────────────────────
async function fetchStatus() {
  isLoading.value = true;
  try {
    const { ok, data } = await apiFetch(`${baseUrl.value}/status`);
    if (ok) status.value = data.status || 'unknown';
  } finally {
    isLoading.value = false;
  }
}

// ─── Reconnect / QR ──────────────────────────────────────────────────────────
async function reconnect() {
  isLoading.value = true;
  qrCode.value = null;
  showQr.value = false;
  try {
    if (isEvoApi.value) {
      const { ok, data } = await apiFetch(`${baseUrl.value}/reconnect`, {
        method: 'POST',
      });
      if (ok && data.qr_code) {
        qrCode.value = data.qr_code;
        showQr.value = true;
      } else if (ok) {
        // Sem QR ainda — buscar separado
        await fetchQrCode();
      } else {
        showAlert(t('INBOX_MGMT.INSTANCE.RECONNECT_ERROR'));
      }
    } else {
      // WAHA: reconnect reinicia a sessão, depois busca QR
      const { ok } = await apiFetch(`${baseUrl.value}/reconnect`, {
        method: 'POST',
      });
      if (ok) {
        setTimeout(() => fetchQrCode(), 2000);
      } else {
        showAlert(t('INBOX_MGMT.INSTANCE.RECONNECT_ERROR'));
      }
    }
    await fetchStatus();
  } finally {
    isLoading.value = false;
  }
}

async function fetchQrCode() {
  const { ok, data } = await apiFetch(`${baseUrl.value}/qrcode`);
  if (ok && data.qr_code) {
    qrCode.value = data.qr_code;
    showQr.value = true;
  } else {
    showAlert(t('INBOX_MGMT.INSTANCE.QR_ERROR'));
  }
}

// ─── Disconnect ───────────────────────────────────────────────────────────────
async function disconnect() {
  if (!window.confirm(t('INBOX_MGMT.INSTANCE.DISCONNECT_CONFIRM'))) return; // eslint-disable-line no-restricted-globals, no-alert
  isLoading.value = true;
  try {
    const { ok } = await apiFetch(`${baseUrl.value}/disconnect`, {
      method: 'POST',
    });
    if (ok) {
      showAlert(t('INBOX_MGMT.INSTANCE.DISCONNECT_SUCCESS'));
      await fetchStatus();
    } else {
      showAlert(t('INBOX_MGMT.INSTANCE.DISCONNECT_ERROR'));
    }
  } finally {
    isLoading.value = false;
  }
}

// ─── Settings ─────────────────────────────────────────────────────────────────
async function fetchSettings() {
  if (!isEvoApi.value) return;
  const { ok, data } = await apiFetch(`${baseUrl.value}/settings`);
  if (ok && data) {
    settingsData.value = {
      rejectCall: data.rejectCall ?? false,
      msgCall: data.msgCall ?? '',
      groupsIgnore: data.groupsIgnore ?? false,
      alwaysOnline: data.alwaysOnline ?? false,
      readMessages: data.readMessages ?? false,
      readStatus: data.readStatus ?? false,
    };
  }
}

async function saveSettings() {
  savingSettings.value = true;
  try {
    const { ok } = await apiFetch(`${baseUrl.value}/settings`, {
      method: 'PATCH',
      body: JSON.stringify(settingsData.value),
    });
    if (ok) {
      showAlert(t('INBOX_MGMT.INSTANCE.SETTINGS_SAVED'));
    } else {
      showAlert(t('INBOX_MGMT.INSTANCE.SETTINGS_ERROR'));
    }
  } finally {
    savingSettings.value = false;
  }
}

// ─── Proxy ────────────────────────────────────────────────────────────────────
async function saveProxy() {
  if (!isEvoApi.value) return;
  savingProxy.value = true;
  try {
    const { ok } = await apiFetch(`${baseUrl.value}/proxy`, {
      method: 'PATCH',
      body: JSON.stringify(proxyData.value),
    });
    if (ok) {
      showAlert(t('INBOX_MGMT.INSTANCE.PROXY_SAVED'));
    } else {
      showAlert(t('INBOX_MGMT.INSTANCE.PROXY_ERROR'));
    }
  } finally {
    savingProxy.value = false;
  }
}

// ─── Lifecycle ───────────────────────────────────────────────────────────────
onMounted(async () => {
  await fetchStatus();
  if (isEvoApi.value) await fetchSettings();
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="p-6 max-w-2xl">
    <!-- Header com status -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.INSTANCE.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mt-0.5">
          {{ $t('INBOX_MGMT.INSTANCE.SUBTITLE') }}
        </p>
      </div>
      <span
        class="px-3 py-1 rounded-full text-xs font-medium"
        :class="statusClass"
      >
        {{ statusLabel }}
      </span>
    </div>

    <!-- Ações rápidas -->
    <div class="flex gap-3 mb-8">
      <button
        class="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-n-weak text-n-slate-12 hover:bg-n-alpha-2 transition-colors disabled:opacity-50"
        :disabled="isLoading"
        @click="fetchStatus"
      >
        <span :class="{ 'animate-spin': isLoading }">↻</span>
        {{ $t('INBOX_MGMT.INSTANCE.CHECK_STATUS') }}
      </button>

      <button
        class="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-n-brand text-white hover:bg-n-brand/90 transition-colors disabled:opacity-50"
        :disabled="isLoading"
        @click="reconnect"
      >
        {{ $t('INBOX_MGMT.INSTANCE.RECONNECT') }}
      </button>

      <button
        class="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-r-300 text-r-600 hover:bg-r-50 transition-colors disabled:opacity-50"
        :disabled="isLoading"
        @click="disconnect"
      >
        {{ $t('INBOX_MGMT.INSTANCE.DISCONNECT') }}
      </button>
    </div>

    <!-- QR Code (após reconnect) -->
    <div
      v-if="showQr && qrCode"
      class="mb-8 flex flex-col items-center gap-3 p-6 rounded-xl border border-n-weak"
    >
      <p class="text-sm font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.INSTANCE.SCAN_QR') }}
      </p>
      <img
        :src="qrCode"
        alt="QR Code"
        class="w-52 h-52 object-contain rounded-lg"
      />
      <button class="text-xs text-n-brand underline" @click="fetchQrCode">
        {{ $t('INBOX_MGMT.INSTANCE.REFRESH_QR') }}
      </button>
    </div>

    <!-- Configurações Avançadas (Evolution API apenas) -->
    <div v-if="isEvoApi" class="mb-8">
      <h4 class="text-sm font-semibold text-n-slate-12 mb-4">
        {{ $t('INBOX_MGMT.INSTANCE.ADVANCED_SETTINGS') }}
      </h4>

      <div class="flex flex-col gap-4 p-4 rounded-xl border border-n-weak">
        <!-- Rejeitar chamadas -->
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.INSTANCE.REJECT_CALLS') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ $t('INBOX_MGMT.INSTANCE.REJECT_CALLS_DESC') }}
            </p>
          </div>
          <input
            v-model="settingsData.rejectCall"
            type="checkbox"
            class="w-4 h-4 rounded accent-n-brand"
          />
        </div>

        <!-- Mensagem de chamada rejeitada -->
        <div v-if="settingsData.rejectCall">
          <label class="block mb-1 text-xs font-medium text-n-slate-12">{{
            $t('INBOX_MGMT.INSTANCE.REJECT_CALL_MSG')
          }}</label>
          <input
            v-model="settingsData.msgCall"
            type="text"
            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:border-n-brand"
            :placeholder="$t('INBOX_MGMT.INSTANCE.REJECT_CALL_MSG_PLACEHOLDER')"
          />
        </div>

        <!-- Ignorar grupos -->
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.INSTANCE.IGNORE_GROUPS') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ $t('INBOX_MGMT.INSTANCE.IGNORE_GROUPS_DESC') }}
            </p>
          </div>
          <input
            v-model="settingsData.groupsIgnore"
            type="checkbox"
            class="w-4 h-4 rounded accent-n-brand"
          />
        </div>

        <!-- Sempre online -->
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.INSTANCE.ALWAYS_ONLINE') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ $t('INBOX_MGMT.INSTANCE.ALWAYS_ONLINE_DESC') }}
            </p>
          </div>
          <input
            v-model="settingsData.alwaysOnline"
            type="checkbox"
            class="w-4 h-4 rounded accent-n-brand"
          />
        </div>

        <!-- Marcar como lida -->
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.INSTANCE.READ_MESSAGES') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ $t('INBOX_MGMT.INSTANCE.READ_MESSAGES_DESC') }}
            </p>
          </div>
          <input
            v-model="settingsData.readMessages"
            type="checkbox"
            class="w-4 h-4 rounded accent-n-brand"
          />
        </div>

        <!-- Enviar status de leitura -->
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.INSTANCE.READ_STATUS') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ $t('INBOX_MGMT.INSTANCE.READ_STATUS_DESC') }}
            </p>
          </div>
          <input
            v-model="settingsData.readStatus"
            type="checkbox"
            class="w-4 h-4 rounded accent-n-brand"
          />
        </div>

        <button
          class="self-end px-4 py-2 rounded-lg bg-n-brand text-white text-sm font-medium hover:bg-n-brand/90 transition-colors disabled:opacity-50"
          :disabled="savingSettings"
          @click="saveSettings"
        >
          {{ savingSettings ? '...' : $t('INBOX_MGMT.INSTANCE.SAVE_SETTINGS') }}
        </button>
      </div>
    </div>

    <!-- Proxy (Evolution API apenas) -->
    <div v-if="isEvoApi" class="mb-6">
      <h4 class="text-sm font-semibold text-n-slate-12 mb-4">
        {{ $t('INBOX_MGMT.INSTANCE.PROXY_SETTINGS') }}
      </h4>

      <div class="flex flex-col gap-4 p-4 rounded-xl border border-n-weak">
        <!-- Habilitar proxy -->
        <div class="flex items-center justify-between">
          <p class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.INSTANCE.PROXY_ENABLE') }}
          </p>
          <input
            v-model="proxyData.enabled"
            type="checkbox"
            class="w-4 h-4 rounded accent-n-brand"
          />
        </div>

        <template v-if="proxyData.enabled">
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block mb-1 text-xs font-medium text-n-slate-12">{{
                $t('INBOX_MGMT.INSTANCE.PROXY_HOST')
              }}</label>
              <input
                v-model="proxyData.host"
                type="text"
                placeholder="127.0.0.1"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:border-n-brand"
              />
            </div>
            <div>
              <label class="block mb-1 text-xs font-medium text-n-slate-12">{{
                $t('INBOX_MGMT.INSTANCE.PROXY_PORT')
              }}</label>
              <input
                v-model="proxyData.port"
                type="text"
                placeholder="8080"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:border-n-brand"
              />
            </div>
          </div>

          <div>
            <label class="block mb-1 text-xs font-medium text-n-slate-12">{{
              $t('INBOX_MGMT.INSTANCE.PROXY_PROTOCOL')
            }}</label>
            <select
              v-model="proxyData.protocol"
              class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:border-n-brand"
            >
              <option value="http">HTTP</option>
              <option value="https">HTTPS</option>
              <option value="socks5">SOCKS5</option>
            </select>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block mb-1 text-xs font-medium text-n-slate-12">{{
                $t('INBOX_MGMT.INSTANCE.PROXY_USERNAME')
              }}</label>
              <input
                v-model="proxyData.username"
                type="text"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:border-n-brand"
              />
            </div>
            <div>
              <label class="block mb-1 text-xs font-medium text-n-slate-12">{{
                $t('INBOX_MGMT.INSTANCE.PROXY_PASSWORD')
              }}</label>
              <input
                v-model="proxyData.password"
                type="password"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:border-n-brand"
              />
            </div>
          </div>
        </template>

        <button
          class="self-end px-4 py-2 rounded-lg bg-n-brand text-white text-sm font-medium hover:bg-n-brand/90 transition-colors disabled:opacity-50"
          :disabled="savingProxy"
          @click="saveProxy"
        >
          {{ savingProxy ? '...' : $t('INBOX_MGMT.INSTANCE.SAVE_PROXY') }}
        </button>
      </div>
    </div>
  </div>
</template>
